import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Content, LogoMotorway } from '@motorway/motorway-storybook-cra';
import { Button } from '@motorway/mw-highway-code';

import cypressIds from 'CypressId';

import { GA_TAGS } from 'Utilities/analytics';
import { useFeatureFlags } from 'Utilities/hooks';
import { applyCypressData } from 'Utilities/index';
import { PRO_URL } from 'Utilities/urls';
import useInView from 'Utilities/useInView';

import Texts from '../../../../../texts.json';
import useShowNewHomepage from '../../../homepage/Hooks/useShowNewHomepage';
import LogoPress from '../LogoPress/LogoPress';

import { aboutRoutes, ancillaryRoutes, applicationRoutes, pressRoutes, siteRoutes, socialRoutes } from './routes';

import styles from './FooterLinks.scss';

const LocalT = new MDText(Texts);

const texts = {
  ctaPro: LocalT.translate('footerView.pro.button') as string,

  disclaimerDetail: LocalT.translate('footerView.company.disclaimer.detail') as string,
  disclaimerTerms: LocalT.translate('footerView.company.disclaimer.title', { terms: <Link to="/terms#pricing-claims">Terms</Link> }) as string,
  disclaimerTermsDoubleAsterisk: LocalT.translate('footerView.company.disclaimer.doubleAsterisk', { link: <a href="/claim" target="_blank">motorway.co.uk/claim</a> }) as string,
  disclaimerTrackerDetail: LocalT.translate('footerView.company.disclaimer.trackerDetail') as string,

  footerAbout: LocalT.translate('footerView.siteLinks.about') as string,
  footerPressTitle: LocalT.translate('footerView.press.title') as string,
  footerSelling: LocalT.translate('footerView.siteLinks.selling') as string,
};

type Props = {
  showDisclaimer: boolean,
  showDisclaimerDetail: boolean,
  showTrackerDisclaimer: boolean,
};

const getLink = (page: {
  external?: boolean,
  link: string,
  newNavigation?: boolean,
  title: string,
}) => (
  page?.external
    ? (<a href={page.link} rel="noreferrer noopener" target="_blank" title={page.title}>
      {page.title}
    </a>)
    : (<Link to={page.link}>
      {page.title}
    </Link>)
);

const FooterLinks = ({ showDisclaimer, showDisclaimerDetail, showTrackerDisclaimer }: Props) => {
  const [loadImages, setLoadImages] = useState<Boolean>(false);
  const $container = useRef();
  const { showFooterAppLinks } = useFeatureFlags() || {};

  const { showNewHomepage } = useShowNewHomepage();

  const visible = useInView($container, {
    disconnectOnVisible: true,
    rootMargin: '400px',
  });

  const siteAndAboutRoutes = [{
    content: aboutRoutes,
    title: texts.footerAbout,
  }, {
    content: siteRoutes,
    title: texts.footerSelling,
  }];

  useEffect(() => {
    if (visible) {
      setLoadImages(true);
    }
  }, [visible]);

  return (
    <div className={cx(styles.footer)} {...applyCypressData(cypressIds.sections.footerLinks)}>
      <Content
        ref={$container}
        center
        className={styles.press}
        element="section"
        wrapper={{ className: cx(styles.component, styles.pressBackground) }}
      >
        <h4>
          <span data-nosnippet>
            { texts.footerPressTitle }
          </span>
        </h4>

        <ul>
          {pressRoutes.map((p) => (
            <li key={p.name}>
              <LogoPress publication={loadImages ? p.name : ''} />
            </li>
          ))}
        </ul>
      </Content>

      <Content
        className={cx(styles.siteLinks, { [styles.siteLinksFeature]: showFooterAppLinks })}
        element="section"
        wrapper={{ className: cx(styles.component, styles.siteLinksWrapper) }}
      >
        <div className={cx(styles.social, { [styles.flexWrapper]: showFooterAppLinks })}>
          <div className={cx({ [styles.flexColumn]: showFooterAppLinks })}>
            <LogoMotorway className={cx(styles.logo, { [styles.logoFeature]: showFooterAppLinks })} />
            <ul className={cx(styles.socialLinks, { [styles.socialLinksFeature]: showFooterAppLinks })}>
              {socialRoutes.map(({ icon: IconComponent, link, name, title }) => (
                <li key={name}>
                  <a
                    className={name}
                    href={link}
                    onClick={() => GA_TAGS.FOOTER_SOCIAL_LINK_CLICKED()}
                    rel="noopener"
                    title={title}
                  > {<IconComponent />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          {
            showFooterAppLinks && (
              <div className={styles.flexRowColumn}>
                <ul className={cx({ [styles.appStoreLinks]: showFooterAppLinks })}>
                  {
                    applicationRoutes.map(({ description, icon, link, name }) => (
                      <li key={name}>
                        <a className={styles.link} href={link} target='_blank'>
                          <img alt={description} height={icon.height} loading='lazy' src={icon.src} width={icon.width}/>
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            )
          }
        </div>
        {
          siteAndAboutRoutes
            .map((section) => (
              <div key={section.title} className={styles.siteAndAboutLinks}>
                <h4 className={cx({ [styles.headingFeature]: showFooterAppLinks })}>
                  <span data-nosnippet>
                    { section.title }
                  </span>
                </h4>
                <ul>
                  { section.content.map((c) => (
                    <li key={c.title}>
                      { getLink(c) }
                    </li>
                  ))}
                </ul>
              </div>
            ))
        }
      </Content>

      <Content
        center
        className={styles.extraLinks}
        element="section"
        wrapper={{ className: styles.component }}
      >
        <div data-theme="dark">
          <Button
            data-thc-button
            fullWidth
            as="a"
            href={PRO_URL}
            icon="chevron"
            label= {texts.ctaPro}
            onClick={() => GA_TAGS.FOOTER_DEALER_BUTTON_CLICKED()}
            target="_blank"
            variant="secondary"
          />
        </div>
        <ul className={styles.ancillaryPages}>
          {
            ancillaryRoutes
              .map((a) => (
                <li key={a.title}>
                  { getLink(a) }
                </li>
              ))
          }
        </ul>
        <p className={styles.copyright}>
          {LocalT.translate('footerView.company.copyrightMessage', {
            year: new Date().getFullYear(),
          })}
        </p>
        { showDisclaimer && <p className={styles.disclaimer}>{ texts.disclaimerTerms }</p> }
        { showDisclaimerDetail && <p className={styles.detail}>{ texts.disclaimerDetail }</p> }
        { showDisclaimer && showNewHomepage
          && <p className={styles.disclaimer}>{ texts.disclaimerTermsDoubleAsterisk }</p>
        }
        { showTrackerDisclaimer && <p className={styles.detail}>{ texts.disclaimerTrackerDetail }</p> }
      </Content>
    </div>
  );
};

export default FooterLinks;
